import {
  renderChart
} from "./chunk-4RHVTNRD.js";

// src/inlineAction.ts
function parseInlineArgs(src, self) {
  const trimmed = src.trim();
  if (trimmed === "") return [];
  const parts = [];
  let cur = "", quote = "";
  for (let i = 0; i < trimmed.length; i++) {
    const ch = trimmed[i];
    if (quote) {
      if (ch === "\\" && i + 1 < trimmed.length) {
        cur += ch + trimmed[++i];
        continue;
      }
      if (ch === quote) quote = "";
      cur += ch;
    } else if (ch === "'" || ch === '"') {
      quote = ch;
      cur += ch;
    } else if (ch === ",") {
      parts.push(cur);
      cur = "";
    } else cur += ch;
  }
  parts.push(cur);
  const out = [];
  for (const p of parts) {
    const t = p.trim();
    if (t === "self" || t === "This") out.push(self);
    else if (/^'([^']*)'$/.test(t) || /^"([^"]*)"$/.test(t)) out.push(t.slice(1, -1));
    else if (/^-?\d+(\.\d+)?$/.test(t)) out.push(Number(t));
    else if (t === "true") out.push(true);
    else if (t === "false") out.push(false);
    else if (t === "null") out.push(null);
    else return null;
  }
  return out;
}
function execInlineAction(raw, self, onError) {
  const code = raw.replace(/^javascript:/i, "").trim().replace(/;+$/, "");
  const m = /^([A-Za-z_$][\w$]*)\s*\((.*)\)$/s.exec(code);
  if (!m) return false;
  const fnName = m[1];
  const fn = (typeof window !== "undefined" ? window : globalThis)[fnName];
  if (typeof fn !== "function") return false;
  const args = parseInlineArgs(m[2], self);
  if (args === null) return false;
  try {
    fn(...args);
    return true;
  } catch (err) {
    try {
      onError?.(err);
    } catch {
    }
    return false;
  }
}

// src/wire/groupTabs.ts
function wireGroupTabs(chassis, hooks) {
  const tablist = chassis.querySelector('[role="tablist"]');
  const groupsEl = chassis.querySelector(".xone-groups");
  if (!groupsEl) return () => {
  };
  const panels = Array.from(
    groupsEl.querySelectorAll(':scope > .xone-group[role="tabpanel"]')
  );
  const tabs = tablist ? Array.from(tablist.querySelectorAll('[role="tab"]')) : [];
  if (panels.length === 0) return () => {
  };
  const panelById = (id) => panels.find((p) => p.dataset["groupId"] === id);
  const tabById = (id) => tabs.find((t) => t.dataset["groupId"] === id);
  const nameOf = (id) => panelById(id)?.dataset["group"] ?? "";
  const currentActiveId = () => {
    const vis = panels.find((p) => !p.hidden);
    return vis?.dataset["groupId"] ?? null;
  };
  function activate(id, opts = {}) {
    const { focus = false, fireEvents = true } = opts;
    const prevId = currentActiveId();
    if (prevId === id) return;
    if (fireEvents && prevId && !hooks.canLeaveGroup(prevId, nameOf(prevId))) {
      return;
    }
    if (fireEvents && prevId) {
      hooks.fireGroupEvent("onlostfocus", prevId, nameOf(prevId));
    }
    for (const p of panels) p.hidden = p.dataset["groupId"] !== id;
    for (const t of tabs) {
      const sel = t.dataset["groupId"] === id;
      t.setAttribute("aria-selected", String(sel));
      t.tabIndex = sel ? 0 : -1;
    }
    const panel = panelById(id);
    if (panel) hooks.hydratePanel(panel);
    if (fireEvents) hooks.fireGroupEvent("onfocus", id, nameOf(id));
    if (focus) tabById(id)?.focus();
    hooks.persistActiveGroup(id);
  }
  const onClick = (e) => {
    const target = e.target;
    const tab = target?.closest('[role="tab"]');
    if (tab && tablist?.contains(tab) && tab.dataset["groupId"]) {
      activate(tab.dataset["groupId"], { focus: true });
    }
  };
  const onKeydown = (e) => {
    if (tabs.length === 0) return;
    const idx = tabs.findIndex(
      (t) => t.getAttribute("aria-selected") === "true"
    );
    let next = -1;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = (idx + 1) % tabs.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        next = (idx - 1 + tabs.length) % tabs.length;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = tabs.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    const id = tabs[next]?.dataset["groupId"];
    if (id) activate(id, { focus: true });
  };
  tablist?.addEventListener("click", onClick);
  tablist?.addEventListener("keydown", onKeydown);
  const swipeEnabled = chassis.dataset["groupSwipe"] === "true";
  let touchX = 0;
  let touchY = 0;
  const onTouchStart = (e) => {
    const t = e.changedTouches[0];
    if (t) {
      touchX = t.clientX;
      touchY = t.clientY;
    }
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - touchX;
    const dy = t.clientY - touchY;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
    const order = panels.map((p) => p.dataset["groupId"]);
    const cur = currentActiveId();
    const idx = cur ? order.indexOf(cur) : 0;
    const nextIdx = dx < 0 ? idx + 1 : idx - 1;
    if (nextIdx >= 0 && nextIdx < order.length) {
      activate(order[nextIdx], { focus: false });
    }
  };
  if (swipeEnabled) {
    groupsEl.addEventListener("touchstart", onTouchStart, { passive: true });
    groupsEl.addEventListener("touchend", onTouchEnd, { passive: true });
  }
  const isManualTabs = chassis.dataset["manualTabs"] === "true";
  if (!isManualTabs) {
    const saved = hooks.readActiveGroup();
    if (saved && panelById(saved) && saved !== currentActiveId()) {
      activate(saved, { fireEvents: false });
    }
  }
  chassis.__xoneActivateGroup = (id) => activate(id, { focus: false });
  return () => {
    tablist?.removeEventListener("click", onClick);
    tablist?.removeEventListener("keydown", onKeydown);
    if (swipeEnabled) {
      groupsEl.removeEventListener("touchstart", onTouchStart);
      groupsEl.removeEventListener("touchend", onTouchEnd);
    }
    delete chassis.__xoneActivateGroup;
  };
}

// src/wire/drawer.ts
function wireDrawer(host, hooks) {
  const asideEl = host.querySelector(".xone-drawer");
  const scrimEl = host.querySelector("[data-drawer-scrim]");
  if (!asideEl || !scrimEl) return () => {
  };
  const aside = asideEl;
  const scrim = scrimEl;
  const coll = host.closest(".xone-coll");
  const groupName = aside.querySelector(".xone-group")?.dataset["group"] ?? "";
  let locked = null;
  const isOpen = () => aside.classList.contains("is-open");
  const focusFirst = () => {
    aside.querySelector(
      "[autofocus],button,[href],input,select,textarea,[tabindex]"
    )?.focus();
  };
  const setBackgroundInert = (on) => {
    if (!coll) return;
    for (const child of Array.from(coll.children)) {
      if (child === host) continue;
      child.inert = on;
    }
  };
  const finishOpen = (open) => {
    if (!open) {
      aside.hidden = true;
      scrim.hidden = true;
    }
    hooks.fireGroupEvent(open ? "ondraweropened" : "ondrawerclosed", groupName, {
      slideOffset: open ? 1 : 0
    });
    hooks.fireGroupEvent("ondrawerstatechanged", groupName, { state: "idle" });
    if (open) focusFirst();
  };
  function setOpen(open) {
    if (locked === "open" && !open) return;
    if (locked === "closed" && open) return;
    if (open === isOpen()) return;
    aside.hidden = false;
    scrim.hidden = false;
    void aside.offsetWidth;
    aside.classList.toggle("is-open", open);
    scrim.classList.toggle("is-open", open);
    aside.inert = !open;
    setBackgroundInert(open);
    hooks.fireGroupEvent("ondrawerstatechanged", groupName, { state: "settling" });
    let done = false;
    const onEnd = (e) => {
      if (e.target !== aside || e.propertyName !== "transform") return;
      if (done) return;
      done = true;
      aside.removeEventListener("transitionend", onEnd);
      finishOpen(open);
    };
    aside.addEventListener("transitionend", onEnd);
    setTimeout(() => {
      if (done) return;
      done = true;
      aside.removeEventListener("transitionend", onEnd);
      finishOpen(open);
    }, 350);
  }
  const onScrim = () => setOpen(false);
  const onKey = (e) => {
    if (e.key === "Escape" && isOpen()) setOpen(false);
  };
  scrim.addEventListener("click", onScrim);
  host.ownerDocument.addEventListener("keydown", onKey);
  const handle = {
    open: () => setOpen(true),
    close: () => setOpen(false),
    toggle: () => setOpen(!isOpen()),
    isOpen,
    lock: (closeState) => {
      locked = closeState ? "closed" : "open";
    },
    unlock: () => {
      locked = null;
    }
  };
  host.__xoneDrawer = handle;
  return () => {
    scrim.removeEventListener("click", onScrim);
    host.ownerDocument.removeEventListener("keydown", onKey);
    delete host.__xoneDrawer;
  };
}

// src/dvSafeEval.ts
var DV_TOKEN = /\s+|_coerce|row|==|!=|>=|<=|&&|\|\||[()[\]!<>]|'[^']*'|"[^"]*"|\d+(?:\.\d+)?|[A-Za-z_$][\w$]*/g;
function coerceDv(v) {
  return v === null || v === void 0 ? 0 : v;
}
function compileTranslatedDv(js) {
  const toks = [];
  DV_TOKEN.lastIndex = 0;
  let m;
  let cur = 0;
  while ((m = DV_TOKEN.exec(js)) !== null) {
    if (m.index !== cur) {
      throw new Error(`dvSafeEval: caracter no soportado en pos ${cur}: '${js.slice(cur, m.index)}'`);
    }
    cur = m.index + m[0].length;
    if (!/^\s+$/.test(m[0])) toks.push(m[0]);
  }
  if (cur !== js.length) {
    throw new Error(`dvSafeEval: caracteres sobrantes al final: '${js.slice(cur)}'`);
  }
  let i = 0;
  const peek = () => toks[i];
  const eat = (expected) => {
    const x = toks[i++];
    if (x === void 0) {
      throw new Error(`dvSafeEval: fin inesperado de expresi\xF3n (se esperaba '${expected ?? "token"}')`);
    }
    if (expected !== void 0 && x !== expected) {
      throw new Error(`dvSafeEval: se esperaba '${expected}' pero lleg\xF3 '${x}'`);
    }
    return x;
  };
  const strLit = (t) => t[0] === "'" || t[0] === '"' ? t.slice(1, -1) : t;
  function primary() {
    const t = eat();
    if (t === "(") {
      const e = orExpr();
      eat(")");
      return e;
    }
    if (t === "_coerce") {
      eat("(");
      eat("row");
      eat("[");
      const k = strLit(eat());
      eat("]");
      eat(")");
      return (row) => coerceDv(row[k]);
    }
    if (t === "row") {
      eat("[");
      const k = strLit(eat());
      eat("]");
      return (row) => row[k];
    }
    if (t === "null") return () => null;
    if (t === "undefined") return () => void 0;
    if (t === "true") return () => true;
    if (t === "false") return () => false;
    if (t[0] === "'" || t[0] === '"') {
      const s = t.slice(1, -1);
      return () => s;
    }
    if (/^\d/.test(t)) {
      const n = Number(t);
      return () => n;
    }
    return () => void 0;
  }
  function unary() {
    if (peek() === "!") {
      eat("!");
      const e = unary();
      return (row) => !e(row);
    }
    return primary();
  }
  function cmp() {
    let left = unary();
    for (; ; ) {
      const op = peek();
      if (op !== "==" && op !== "!=" && op !== ">" && op !== "<" && op !== ">=" && op !== "<=") break;
      eat();
      const right = unary();
      const l = left;
      switch (op) {
        // eslint-disable-next-line eqeqeq
        case "==":
          left = (row) => l(row) == right(row);
          break;
        // eslint-disable-next-line eqeqeq
        case "!=":
          left = (row) => l(row) != right(row);
          break;
        case ">":
          left = (row) => l(row) > right(row);
          break;
        case "<":
          left = (row) => l(row) < right(row);
          break;
        case ">=":
          left = (row) => l(row) >= right(row);
          break;
        default:
          left = (row) => l(row) <= right(row);
          break;
      }
    }
    return left;
  }
  function andExpr() {
    let left = cmp();
    while (peek() === "&&") {
      eat("&&");
      const right = cmp();
      const l = left;
      left = (row) => Boolean(l(row)) && Boolean(right(row));
    }
    return left;
  }
  function orExpr() {
    let left = andExpr();
    while (peek() === "||") {
      eat("||");
      const right = andExpr();
      const l = left;
      left = (row) => Boolean(l(row)) || Boolean(right(row));
    }
    return left;
  }
  const fn = orExpr();
  if (i !== toks.length) {
    throw new Error(`dvSafeEval: token inesperado '${peek()}'`);
  }
  return fn;
}

// src/XoneIsland.ts
var RUNTIME_KEY = "__xoneIslandRuntime";
var CONTENTS_ITEM_CHANGED_EVENT = "xone:contents-item-changed";
var DV_ROW_CACHE = /* @__PURE__ */ new Map();
function compileDvRow(js) {
  let fn = DV_ROW_CACHE.get(js);
  if (!fn) {
    try {
      fn = compileTranslatedDv(js);
    } catch {
      fn = () => false;
    }
    DV_ROW_CACHE.set(js, fn);
  }
  return fn;
}
var NON_READONLY_INPUT_TYPES = /* @__PURE__ */ new Set([
  "checkbox",
  "radio",
  "button",
  "submit",
  "reset",
  "file",
  "range",
  "color",
  "image",
  "hidden"
]);
function setEditDisabledState(el, disabled) {
  const tag = el.tagName;
  const type = (el.type ?? "").toLowerCase();
  const useReadonly = tag === "TEXTAREA" || tag === "INPUT" && !NON_READONLY_INPUT_TYPES.has(type);
  if (useReadonly) {
    if (disabled) {
      el.setAttribute("readonly", "");
      el.setAttribute("inert", "");
    } else {
      el.removeAttribute("readonly");
      el.removeAttribute("inert");
    }
  } else {
    if (disabled) el.setAttribute("disabled", "");
    else el.removeAttribute("disabled");
  }
}
function trackNodePromise(p) {
  try {
    const g = globalThis;
    if (!g.__xonePendingWrites) g.__xonePendingWrites = /* @__PURE__ */ new Set();
    const set = g.__xonePendingWrites;
    set.add(p);
    const remove = () => {
      set.delete(p);
    };
    p.then(remove, remove);
  } catch {
  }
  return p;
}
var HYDRATED = /* @__PURE__ */ Symbol("xone-island-hydrated");
var ELEMENT_TAG = "xone-island";
var __xoneLoadedScripts = /* @__PURE__ */ new Map();
function loadExternalScriptOnce(src) {
  const cached = __xoneLoadedScripts.get(src);
  if (cached) return cached;
  const p = new Promise((resolve) => {
    const already = document.querySelector(
      `script[data-xone-widget-src="${CSS.escape(src)}"]`
    );
    if (already) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.setAttribute("data-xone-widget-src", src);
    s.onload = () => resolve();
    s.onerror = () => resolve();
    document.head.appendChild(s);
  });
  __xoneLoadedScripts.set(src, p);
  return p;
}
function resolveWidgetMacros(runtime, value) {
  let out = value;
  const api = runtime.api;
  out = out.replace(/##FLD_([A-Za-z0-9_]+)##/g, (_m, field) => {
    try {
      const v = api.self?.getValue?.(field);
      return v == null ? "" : String(v);
    } catch {
      return "";
    }
  });
  try {
    if (api.appData?.expandMacros) out = api.appData.expandMacros(out);
  } catch {
  }
  return out;
}
var XoneIslandElement = class _XoneIslandElement extends HTMLElement {
  [HYDRATED] = false;
  cleanup = [];
  /** Re-carga del contents (la fija wireContents). */
  _reloadContents = null;
  /** Re-render del contents SIN recargar del backend (la fija wireContents). */
  _rerenderContents = null;
  /** Evita doble wiring del click de selecteditem en re-cargas. */
  _selectedItemWired = false;
  /** Evita doble wiring del sync de scroll de la cabecera (header-contents). */
  _headerSyncWired = false;
  /** Evita doble wiring del ResizeObserver del ancho de tabla (header-contents). */
  _hcResizeWired = false;
  /**
   * Refresca el control: para un island de contents re-lanza la carga
   * (respetando el filtro runtime de la colección). Entry point de
   * `ui.getView(self).getControl(name).refresh()`.
   */
  refresh() {
    if (this._reloadContents) {
      this._reloadContents();
    }
  }
  /**
   * Re-renderiza el contents desde los items YA cargados en la colección
   * compartida, SIN recargar del backend. Entry point de `ui.refresh(name)`
   * (paridad Android: `Refresh(control)` re-lee la fuente en memoria, no toca
   * la BD; la carga es un paso aparte). El proyecto siempre hace `loadAll`
   * antes (`actualizarDatosContent`), así que los datos están disponibles.
   * Fallback a `refresh()` (reload) si el island aún no cableó el re-render.
   */
  rerender() {
    if (this._rerenderContents) {
      this._rerenderContents();
    } else {
      this.refresh();
    }
  }
  connectedCallback() {
    if (this[HYDRATED]) return;
    const lazy = this.getAttribute("lazy") ?? "interaction";
    if (lazy === "eager") {
      void this.hydrate();
      return;
    }
    if (lazy === "visible") {
      this.observeVisible();
      return;
    }
    this.observeFirstInteraction();
  }
  disconnectedCallback() {
    for (const fn of this.cleanup) {
      try {
        fn();
      } catch {
      }
    }
    this.cleanup = [];
  }
  observeFirstInteraction() {
    const evts = [
      "focus",
      "click",
      "input",
      "change",
      "keydown"
    ];
    const onTrigger = () => {
      void this.hydrate();
      for (const e of evts) this.removeEventListener(e, onTrigger, true);
    };
    for (const e of evts) {
      this.addEventListener(e, onTrigger, { capture: true });
    }
  }
  observeVisible() {
    if (typeof IntersectionObserver === "undefined") {
      void this.hydrate();
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            obs.disconnect();
            void this.hydrate();
            return;
          }
        }
      },
      { rootMargin: "100px" }
    );
    obs.observe(this);
    this.cleanup.push(() => obs.disconnect());
  }
  async hydrate() {
    if (this[HYDRATED]) return;
    const runtime = window[RUNTIME_KEY];
    if (!runtime) {
      return;
    }
    try {
      if ((this.getAttribute("data-type") ?? "").toLowerCase() === "widget") {
        await this.mountWidget(runtime);
      } else {
        this.wireListeners(runtime);
      }
      this[HYDRATED] = true;
      this.setAttribute("hydrated", "");
    } catch (err) {
      try {
        runtime.api.console.error("[xone-island] hydrate failed", err);
      } catch {
      }
    }
  }
  /**
   * Fuerza la hidratación on-demand (R2): usado al activar una pestaña para
   * montar los islands lazy que estaban en un panel `hidden` (display:none),
   * donde el IntersectionObserver (`visible`) y la interacción (`interaction`)
   * nunca disparan. Idempotente.
   */
  hydrateNow() {
    void this.hydrate();
  }
  /**
   * Monta un widget externo declarado con `web-widget` (data-type="widget").
   * Carga el script externo (deduplicado), crea el custom element, resuelve
   * las macros de sus atributos contra el runtime y lo inserta en el host.
   * El DOM lo manipula el RUNTIME (no el código de proyecto, que sigue sin
   * tocar `document`). Ver docs/xone-web-platform/03-web-widget-embedding.md.
   */
  async mountWidget(runtime, attempt = 0) {
    const tag = this.getAttribute("data-widget-tag");
    if (!tag) return;
    const host = this.querySelector(".xone-widget-host");
    if (!host || host.childElementCount > 0) return;
    const src = resolveWidgetMacros(runtime, this.getAttribute("data-widget-src") ?? "");
    let attrs = {};
    try {
      const raw = this.getAttribute("data-widget-attrs");
      attrs = raw ? JSON.parse(raw) : {};
    } catch {
    }
    const resolved = {};
    for (const [name, value] of Object.entries(attrs)) {
      resolved[name] = resolveWidgetMacros(runtime, String(value));
    }
    const UNRESOLVED = /##[A-Za-z0-9_]+##/;
    const stillPending = UNRESOLVED.test(src) || Object.values(resolved).some((v) => UNRESOLVED.test(v));
    if (stillPending && attempt < 24) {
      const t = window.setTimeout(() => {
        void this.mountWidget(runtime, attempt + 1);
      }, 250);
      this.cleanup.push(() => window.clearTimeout(t));
      return;
    }
    if (src) {
      try {
        await loadExternalScriptOnce(src);
      } catch {
      }
    }
    const el = document.createElement(tag);
    for (const [name, value] of Object.entries(resolved)) {
      try {
        el.setAttribute(name, value);
      } catch {
      }
    }
    host.appendChild(el);
  }
  wireGroupTabs(runtime) {
    const collName = this.getAttribute("data-coll") ?? "";
    const storageKey = `xone-active-group:${collName}`;
    const hooks = {
      fireGroupEvent: (eventName, _groupId, groupName) => {
        if (groupName === "") return;
        const handler = runtime.events[`${groupName}_${eventName}`];
        if (typeof handler === "function") {
          void invokeHandler(runtime, handler, []);
        }
      },
      canLeaveGroup: () => {
        return true;
      },
      persistActiveGroup: (groupId) => {
        try {
          window.sessionStorage?.setItem(storageKey, groupId);
        } catch {
        }
      },
      readActiveGroup: () => {
        try {
          return window.sessionStorage?.getItem(storageKey) ?? null;
        } catch {
          return null;
        }
      },
      hydratePanel: (panel) => {
        const islands = panel.querySelectorAll(ELEMENT_TAG);
        for (const el of islands) {
          if (el instanceof _XoneIslandElement) el.hydrateNow();
        }
      }
    };
    const cleanup = wireGroupTabs(this, hooks);
    this.cleanup.push(cleanup);
  }
  wireDrawer(runtime) {
    const hooks = {
      fireGroupEvent: (eventName, groupName, _detail) => {
        if (groupName === "") return;
        const handler = runtime.events[`${groupName}_${eventName}`];
        if (typeof handler === "function") {
          void invokeHandler(runtime, handler, []);
        }
      }
    };
    const cleanup = wireDrawer(this, hooks);
    this.cleanup.push(cleanup);
  }
  wireListeners(runtime) {
    const eventsSpec = (this.getAttribute("data-events") ?? "").split(",").map((s) => s.trim()).filter((s) => s !== "");
    const propName = this.getAttribute("data-prop") ?? "";
    const propType = (this.getAttribute("data-type") ?? "").toUpperCase();
    for (const spec of eventsSpec) {
      if (spec === "bind") {
        this.wireBind(runtime, propName, propType);
      } else if (spec === "click") {
        this.wireClickInline(runtime, propName);
      } else if (spec === "frame-click") {
        this.wireFrameClick(runtime, propName);
      } else if (spec === "change") {
        this.wireChangeInline(runtime, propName);
      } else if (spec === "contents") {
        this.wireContents(runtime, propName);
      } else if (spec === "group-tabs") {
        this.wireGroupTabs(runtime);
      } else if (spec === "drawer") {
        this.wireDrawer(runtime);
      } else if (spec.startsWith("method:")) {
        const methodName = spec.slice("method:".length);
        this.wireMethod(runtime, propName, methodName);
      }
    }
  }
  /**
   * Resuelve el `api` para el handler de un control que puede vivir en una
   * FILA de un contents. Si el control está dentro de `.xone-contents-row`, el
   * `self` del handler es el OBJETO DE LA FILA (`getCollection(data-src del
   * island host Z).get(data-row-index)`) — p.ej. el botón Móvil de un lead
   * ejecuta `llamarLead` con `self`=ese lead (no el `self` de la página).
   * Fuera de una fila devuelve el `api` de la página sin cambios.
   */
  resolveRowApi(runtime) {
    const row = this.closest(".xone-contents-row");
    if (!row) return runtime.api;
    const host = this.closest('xone-island[data-type="Z"]');
    const src = host?.getAttribute("data-src") ?? "";
    const idx = Number(row.getAttribute("data-row-index") ?? "0");
    const coll = src ? runtime.api.appData.getCollection(src) : null;
    const item = coll?.get?.(idx);
    return item && typeof item === "object" ? { ...runtime.api, self: item, This: item } : runtime.api;
  }
  wireBind(runtime, propName, propType) {
    if (propName === "") return;
    const input = this.querySelector(
      "input,textarea,select"
    );
    if (!input) return;
    const resolveBindTarget = () => {
      const row = this.closest(".xone-contents-row");
      if (!row) return runtime.api.self;
      const host = this.closest('xone-island[data-type="Z"]');
      const src = host?.getAttribute("data-src") ?? "";
      const idx = Number(row.getAttribute("data-row-index") ?? "0");
      const coll = src ? runtime.api.appData.getCollection(src) : null;
      const item = coll?.get?.(idx);
      return item && typeof item === "object" ? item : runtime.api.self;
    };
    let initial = void 0;
    try {
      initial = resolveBindTarget().getValue?.(propName);
    } catch {
    }
    if (initial !== void 0 && initial !== null) {
      if (propType === "NC" && "checked" in input) {
        input.checked = Boolean(initial) && initial !== "0";
      } else {
        input.value = String(initial);
      }
    }
    const onInput = () => {
      const v = propType === "NC" && "checked" in input ? input.checked ? 1 : 0 : input.value;
      try {
        void resolveBindTarget().setValue?.(propName, v);
      } catch (err) {
        runtime.api.console.error("[xone-island] bind setValue", err);
      }
      const changeHandler = this.resolveHandler(runtime, `${propName}_onchange`);
      if (typeof changeHandler === "function") {
        void invokeHandler(runtime, changeHandler, [v], this.resolveRowApi(runtime));
      }
    };
    const ev = propType === "NC" ? "change" : "input";
    input.addEventListener(ev, onInput);
    this.cleanup.push(() => input.removeEventListener(ev, onInput));
    const oea = this.getAttribute("data-oneditoraction");
    if (oea && oea.trim() !== "" && propType !== "NC") {
      const onKeydown = (e) => {
        if (e.key !== "Enter") return;
        const target = resolveBindTarget();
        try {
          void target.setValue?.(propName, input.value);
        } catch {
        }
        execInlineAction(oea, target, (err) => {
          try {
            runtime.api.console.error("[xone-island] oneditoraction", err);
          } catch {
          }
        });
      };
      input.addEventListener("keydown", onKeydown);
      this.cleanup.push(() => input.removeEventListener("keydown", onKeydown));
    }
  }
  /**
   * Resuelve un handler de evento: primero en el módulo de la PÁGINA
   * (`runtime.events`), después en el módulo de la colección del island
   * (`data-coll` → `runtime.eventsByColl`). Los islands anidados en filas
   * de un contents llevan `data-coll` de la coll HIJA, cuyos handlers
   * (onclick/onchange/selecteditem) viven en su propio events module —
   * paridad Android: los eventos de las props de fila pertenecen a la
   * coll del contents, no a la página host.
   */
  resolveHandler(runtime, key) {
    const fromPage = runtime.events[key];
    if (typeof fromPage === "function") return fromPage;
    const collName = this.getAttribute("data-coll") ?? "";
    const mod = collName !== "" ? runtime.eventsByColl?.[collName] : void 0;
    const fromColl = mod?.[key];
    return typeof fromColl === "function" ? fromColl : void 0;
  }
  wireClickInline(runtime, propName) {
    const handlerName = `${propName}_onclick`;
    const handler = this.resolveHandler(runtime, handlerName);
    if (typeof handler !== "function") {
      return;
    }
    const button = this.querySelector("button");
    const target = button ?? this;
    const onClick = (e) => {
      void invokeHandler(runtime, handler, [e], this.resolveRowApi(runtime));
    };
    target.addEventListener("click", onClick);
    this.cleanup.push(() => target.removeEventListener("click", onClick));
  }
  wireFrameClick(runtime, frameName) {
    const handlerName = `${frameName}_onclick`;
    const handler = this.resolveHandler(runtime, handlerName);
    if (typeof handler !== "function") return;
    const frame = this.querySelector(`[data-xone-frame="${frameName}"]`) ?? this;
    frame.style.cursor = "pointer";
    const onClick = (e) => {
      e.stopPropagation();
      trackNodePromise(invokeHandler(runtime, handler, [e]));
    };
    frame.addEventListener("click", onClick);
    this.cleanup.push(() => frame.removeEventListener("click", onClick));
  }
  wireChangeInline(runtime, propName) {
    const handlerName = `${propName}_onchange`;
    const handler = this.resolveHandler(runtime, handlerName);
    if (typeof handler !== "function") return;
    const input = this.querySelector("input,textarea,select");
    if (!input) return;
    const onChange = () => {
      void invokeHandler(runtime, handler, [input.value], this.resolveRowApi(runtime));
    };
    input.addEventListener("change", onChange);
    this.cleanup.push(() => input.removeEventListener("change", onChange));
  }
  wireMethod(runtime, propName, methodName) {
    const handler = this.resolveHandler(runtime, methodName);
    const gotoGroup = this.getAttribute("data-goto-group");
    if (typeof handler !== "function" && gotoGroup === null) return;
    let methodArgs = [];
    const rawArgs = this.getAttribute("data-method-args");
    if (rawArgs) {
      try {
        const parsed = JSON.parse(rawArgs);
        if (Array.isArray(parsed)) methodArgs = parsed;
      } catch {
      }
    }
    const button = this.querySelector("button");
    const target = button ?? this;
    const onClick = () => {
      if (typeof handler === "function") {
        trackNodePromise(invokeHandler(runtime, handler, methodArgs, this.resolveRowApi(runtime)));
      }
      if (gotoGroup !== null) {
        try {
          runtime.api.ui.showGroup?.(gotoGroup);
        } catch {
        }
      }
    };
    target.addEventListener("click", onClick);
    this.cleanup.push(() => target.removeEventListener("click", onClick));
    void propName;
  }
  wireContents(runtime, propName) {
    let srcColl = this.getAttribute("data-src") ?? "";
    let filter = this.getAttribute("data-filter") ?? void 0;
    let sort = this.getAttribute("data-sort") ?? void 0;
    const innerDiv = this.querySelector("div[data-src]");
    if (!srcColl && innerDiv) {
      srcColl = innerDiv.getAttribute("data-src") ?? "";
      filter = filter ?? innerDiv.getAttribute("data-filter") ?? void 0;
      sort = sort ?? innerDiv.getAttribute("data-sort") ?? void 0;
    }
    if (!srcColl) {
      this.setAttribute("data-status", "no-src");
      return;
    }
    const container = innerDiv ?? this;
    const rppAttr = parseInt(container.getAttribute("data-rows-per-page") ?? "", 10);
    const rowsPerPage = Number.isFinite(rppAttr) && rppAttr > 0 ? rppAttr : null;
    const templateEl = container.querySelector(
      "template[data-item-template]"
    );
    const templateHtml = templateEl?.innerHTML ?? "";
    let viewmode = (container.getAttribute("data-viewmode") ?? "recyclerview").toLowerCase();
    const contentsName = this.getAttribute("data-contents-name") ?? propName;
    const bareContentsName = contentsName.replace(/^@/, "");
    if (bareContentsName) {
      const scope = this.closest("[data-xone-frame]") ?? this.parentElement;
      const siblingIsland = scope?.querySelector(
        `xone-island[data-prop="${bareContentsName}"]`
      );
      const siblingVm = siblingIsland?.querySelector("[data-viewmode]");
      if (siblingIsland && siblingIsland !== this && siblingVm) {
        const sv = siblingVm.getAttribute("data-viewmode")?.toLowerCase();
        if (sv) viewmode = sv;
        for (const attr of ["data-columns", "data-orientation", "data-cell-height", "data-bgcolor", "data-chart-type", "data-chart-config", "data-cell-even-color", "data-cell-odd-color"]) {
          const v = siblingVm.getAttribute(attr);
          if (v !== null) container.setAttribute(attr, v);
        }
        container.setAttribute("data-viewmode", viewmode);
        const box = siblingIsland.style;
        if (box.width && !this.style.width) this.style.width = box.width;
        if (box.height && !this.style.height) this.style.height = box.height;
        if (box.flexShrink && !this.style.flexShrink) this.style.flexShrink = box.flexShrink;
        if (box.minHeight && !this.style.minHeight) this.style.minHeight = box.minHeight;
        if (!this.style.width) this.style.width = "100%";
        siblingIsland.style.display = "none";
      }
    }
    const columns = parseInt(container.getAttribute("data-columns") ?? "2", 10);
    const orientation = (container.getAttribute("data-orientation") ?? "vertical").toLowerCase();
    const cellHeight = container.getAttribute("data-cell-height") ?? "";
    const showNoData = container.getAttribute("data-show-no-data") ?? "true";
    const bgcolor = container.getAttribute("data-bgcolor") ?? "";
    this.setAttribute("data-status", "loading");
    const resolvedFilter = this.resolveMacros(runtime, filter);
    const renderFromColl = (coll) => {
      const count = coll.getCount?.() ?? 0;
      container.innerHTML = "";
      this.applyContentsLayout(container, viewmode, columns, orientation, bgcolor);
      if (count === 0) {
        if (showNoData.toLowerCase() !== "false") {
          const empty = document.createElement("div");
          empty.className = "xone-contents-empty";
          empty.textContent = "No hay datos";
          container.appendChild(empty);
        }
        this.setAttribute("data-status", "empty");
        return;
      }
      const items = this.collectItems(coll, count);
      const chartRaw = container.getAttribute("data-chart-config");
      if (chartRaw) {
        try {
          const cfg = JSON.parse(chartRaw);
          renderChart(container, cfg, items);
          this.setAttribute("data-status", "loaded");
          return;
        } catch (e) {
          runtime.api.console.error("[xone-island] chart render failed", e);
        }
      }
      const isExpandView = viewmode === "expanview" || viewmode === "expandview";
      if (isExpandView) {
        this.renderExpandView(runtime, container, templateHtml, items, cellHeight);
      } else {
        for (let i = 0; i < items.length; i++) {
          this.appendContentRow(container, templateHtml, items[i], i, cellHeight);
        }
      }
      this.setAttribute("data-status", "loaded");
      this.wireSelectedItem(runtime, propName, container);
      this.wireHeaderScrollSync(container);
      this.applyHeaderedTableWidths(container);
    };
    const loadContents = async () => {
      try {
        const ready = runtime.api.lifecycleReady;
        if (ready) {
          try {
            await ready;
          } catch {
          }
        }
        const coll = runtime.api.appData.getCollection(srcColl);
        const lifecycleFilter = typeof coll.getFilter === "function" ? coll.getFilter() : "";
        const effectiveFilter = lifecycleFilter && String(lifecycleFilter).trim().length > 0 ? String(lifecycleFilter) : resolvedFilter;
        await coll.loadAll(
          effectiveFilter,
          sort,
          rowsPerPage != null ? { size: rowsPerPage } : void 0
        );
        renderFromColl(coll);
      } catch (err) {
        this.setAttribute("data-status", "error");
        try {
          runtime.api.console.error("[xone-island] contents load failed", err);
        } catch {
        }
        const errObj = err;
        const status = errObj?.status;
        const code = errObj?.code ?? "";
        container.innerHTML = "";
        const placeholder = document.createElement("div");
        placeholder.className = "xone-contents-error";
        placeholder.style.cssText = "padding:16px;text-align:center;color:#999;font-size:13px;";
        if (status === 401 || code.includes("AUTH")) {
          placeholder.textContent = "Inicia sesi\xF3n para ver este contenido";
        } else if (status === 403) {
          placeholder.textContent = "No tienes permisos para ver este contenido";
        } else if (status === 404) {
          placeholder.textContent = "Contenido no disponible";
        } else {
          placeholder.textContent = "Error al cargar datos";
        }
        container.appendChild(placeholder);
      }
    };
    this._reloadContents = () => {
      this.setAttribute("data-status", "loading");
      void loadContents();
    };
    this._rerenderContents = () => {
      try {
        const coll = runtime.api.appData.getCollection(srcColl);
        if ((coll.getCount?.() ?? 0) > 0) {
          renderFromColl(coll);
        } else {
          this.setAttribute("data-status", "loading");
          void loadContents();
        }
      } catch (err) {
        try {
          runtime.api.console.error("[xone-island] rerender failed", err);
        } catch {
        }
      }
    };
    if (typeof document !== "undefined") {
      const onItemChanged = (e) => {
        const detail = e.detail;
        if (detail?.src && detail.src === srcColl && this._rerenderContents) {
          this._rerenderContents();
        }
      };
      document.addEventListener(CONTENTS_ITEM_CHANGED_EVENT, onItemChanged);
      this.cleanup.push(() => document.removeEventListener(CONTENTS_ITEM_CHANGED_EVENT, onItemChanged));
    }
    void loadContents();
  }
  collectItems(coll, count) {
    const items = [];
    for (let i = 0; i < count; i++) {
      const item = coll.get?.(i);
      if (item) items.push(item);
    }
    return items;
  }
  renderExpandView(runtime, container, templateHtml, items, cellHeight) {
    container.classList.add("is-expandview");
    container.style.display = "flex";
    container.style.flexDirection = "column";
    container.style.overflowY = "auto";
    const hasNivel = items.length > 0 && "NIVEL" in items[0];
    const parents = hasNivel ? items.filter((it) => Number(it["NIVEL"]) === 0) : items;
    const childrenByParent = /* @__PURE__ */ new Map();
    if (hasNivel) {
      for (const it of items) {
        if (Number(it["NIVEL"]) !== 0) {
          const pid = it["IDPADRE"] ?? it["ID_PADRE"] ?? "";
          const key = String(pid);
          if (!childrenByParent.has(key)) childrenByParent.set(key, []);
          childrenByParent.get(key).push(it);
        }
      }
    }
    const childSrc = container.getAttribute("data-child-src") ?? "";
    const childFilter = container.getAttribute("data-child-filter") ?? "";
    const childSort = container.getAttribute("data-child-sort") ?? "";
    for (let i = 0; i < parents.length; i++) {
      const item = parents[i];
      const details = document.createElement("details");
      details.className = "xone-expand-group";
      details.setAttribute("data-row-index", String(i));
      const summary = document.createElement("summary");
      summary.className = "xone-expand-summary xone-content-item";
      summary.style.cssText = "cursor:pointer;list-style:none;display:flex;align-items:center;";
      summary.innerHTML = templateHtml;
      this.expandItemFldMacros(summary, item);
      this.bindItemValues(summary, item);
      for (const el of summary.querySelectorAll("[style]")) {
        if (el.style.height && (el.style.height.includes("dvh") || el.style.height.includes("vh"))) {
          el.style.height = "auto";
        }
      }
      details.appendChild(summary);
      const childContainer = document.createElement("div");
      childContainer.className = "xone-expand-children";
      childContainer.style.paddingLeft = "24px";
      details.appendChild(childContainer);
      const parentId = String(item["ID"] ?? i);
      const inlineChildren = childrenByParent.get(parentId);
      if (inlineChildren && inlineChildren.length > 0) {
        for (let j = 0; j < inlineChildren.length; j++) {
          const childRow = document.createElement("article");
          childRow.className = "xone-content-item xone-contents-row xone-expand-child";
          childRow.style.cssText = "cursor:pointer;display:flex;align-items:center;";
          childRow.innerHTML = templateHtml;
          this.expandItemFldMacros(childRow, inlineChildren[j]);
          this.bindItemValues(childRow, inlineChildren[j]);
          for (const el of childRow.querySelectorAll("[style]")) {
            if (el.style.height && (el.style.height.includes("dvh") || el.style.height.includes("vh"))) {
              el.style.height = "auto";
            }
          }
          childRow.setAttribute("data-row-index", String(j));
          childContainer.appendChild(childRow);
        }
      } else if (childSrc) {
        let loaded = false;
        details.addEventListener("toggle", () => {
          if (!details.open || loaded) return;
          loaded = true;
          this.loadExpandChildren(
            runtime,
            childContainer,
            childSrc,
            childFilter,
            childSort,
            templateHtml,
            item,
            cellHeight
          );
        });
      }
      container.appendChild(details);
    }
  }
  bindItemValues(el, item) {
    const propEls = el.querySelectorAll("[data-prop]");
    for (const pel of propEls) {
      const field = pel.getAttribute("data-prop");
      if (!field) continue;
      if (pel.getAttribute("data-xone-type") === "Z") {
        pel.remove();
        continue;
      }
      const value = item[field];
      const imgEarly = pel.tagName === "IMG" ? pel : pel.querySelector("img");
      const str = value === void 0 || value === null ? "" : String(value);
      if (imgEarly) {
        if (str.trim() !== "") {
          imgEarly.style.removeProperty("visibility");
          imgEarly.classList.remove("xone-img-empty");
          imgEarly.src = this.normalizeImgSrc(str);
        } else {
          const existing = imgEarly.getAttribute("src") ?? "";
          const hasValidSrc = existing.trim() !== "" && !existing.includes("##");
          if (!hasValidSrc) this.hideBrokenImage(imgEarly);
        }
        continue;
      }
      if (value === void 0 || value === null) continue;
      const labelEl = pel.querySelector(".xone-label-text");
      if (labelEl) {
        labelEl.textContent = str;
        continue;
      }
      const textEl = pel.querySelector(".xone-text-value");
      if (textEl) {
        textEl.textContent = str;
        continue;
      }
      const input = pel.querySelector("input,textarea");
      if (input) {
        const inp = input;
        if (inp.type === "checkbox" || inp.type === "radio" || pel.getAttribute("data-xone-type") === "NC") {
          inp.checked = str !== "" && str !== "0" && str.toLowerCase() !== "false";
        } else {
          inp.value = str;
        }
        continue;
      }
      if (pel.getAttribute("data-xone-type") === "THTML") {
        pel.innerHTML = str;
        continue;
      }
      if (pel.tagName === "SPAN" || pel.tagName === "DIV") pel.textContent = str;
    }
    const imgs = el.querySelectorAll("img[src]");
    for (const img of imgs) {
      const src = img.getAttribute("src") ?? "";
      if (src.trim() === "" || src.includes("##")) {
        this.hideBrokenImage(img);
        continue;
      }
      if (!src.startsWith("/") && !src.startsWith("http") && !src.startsWith("data:")) {
        img.src = this.normalizeImgSrc(src);
      }
    }
    this.applyRowDisableVisible(el, item);
    this.applyRowDisableEdit(el, item);
  }
  /**
   * Evalúa el `disablevisible` POR-FILA de un `<contents>`: los props con
   * `data-disablevisible` (p.ej. MAP_SEL/MAP_NOSEL con dv `MAP_COLOR=0` / `=1`)
   * se ocultan/muestran según los campos de ESTA fila (`item`), no del `self`
   * de página. Paridad Android `ControlsUtils.isPropertyVisible` por-item. El
   * generador hornea la fórmula ya traducida (`_coerce(row["X"])==0`); se
   * evalúa CSP-safe (`compileTranslatedDv`, sin `new Function`) y se togglea
   * `hidden`. Cubre el propio `el` y sus descendientes. Lo llaman AMBOS paths
   * de fila: `bindItemValues` (expandview) y `appendContentRow`
   * (recyclerview/gridview — p.ej. el menú WebMenuPadre).
   */
  applyRowDisableVisible(el, item) {
    const dvEls = [];
    if (el.hasAttribute("data-disablevisible")) dvEls.push(el);
    dvEls.push(...el.querySelectorAll("[data-disablevisible]"));
    for (const dvEl of dvEls) {
      const js = dvEl.getAttribute("data-disablevisible");
      if (!js) continue;
      let hidden = false;
      try {
        hidden = Boolean(compileDvRow(js)(item));
      } catch {
        hidden = false;
      }
      if (hidden) dvEl.setAttribute("hidden", "");
      else dvEl.removeAttribute("hidden");
    }
  }
  /**
   * Evalúa `disableedit` POR FILA contra el `item` y pone read-only/disabled
   * el control (misma fórmula/evaluador que disablevisible; solo cambia el
   * efecto DOM). Cubre el propio `el` y sus descendientes. Lo llaman AMBOS
   * paths de fila (`bindItemValues`, `appendContentRow`), igual que
   * `applyRowDisableVisible`. Paridad Android `ControlsUtils.isPropertyDisabled`
   * por objeto de fila.
   */
  applyRowDisableEdit(el, item) {
    const deEls = [];
    if (el.hasAttribute("data-disableedit")) deEls.push(el);
    deEls.push(...el.querySelectorAll("[data-disableedit]"));
    for (const deEl of deEls) {
      const js = deEl.getAttribute("data-disableedit");
      if (!js) continue;
      let disabled = false;
      try {
        disabled = Boolean(compileDvRow(js)(item));
      } catch {
        disabled = false;
      }
      setEditDisabledState(deEl, disabled);
    }
  }
  /**
   * Aplica un atributo reactivo ya resuelto sobre un elemento de fila (espejo
   * acotado de `domSync.patchPropAttributeDOM`, sin dependencia del self de
   * página). `title` → texto del span del control (botón/label/valor).
   */
  applyRowReactiveAttr(el, attr, value) {
    if (attr === "title") {
      const span = el.querySelector(
        ".xone-btn-text, .xone-label-text, .xone-text-value"
      );
      if (span) span.textContent = value;
      else el.textContent = value;
      return;
    }
    if (attr === "bgcolor") {
      el.style.backgroundColor = this.argbToCss(value);
      return;
    }
    if (attr === "forecolor") {
      el.style.color = this.argbToCss(value);
      return;
    }
  }
  /** `#AARRGGBB` (ARGB XOne) → `#RRGGBBAA` (CSS). Pasa el resto tal cual. */
  argbToCss(raw) {
    if (raw.length === 9 && raw.startsWith("#")) {
      return `#${raw.slice(3)}${raw.slice(1, 3)}`;
    }
    return raw;
  }
  /**
   * Oculta un <img> sin recurso (src vacío o macro ##FLD_## sin expandir)
   * conservando su caja. Quita el src (evita el GET 404 → glifo de
   * imagen-rota del navegador) y aplica visibility:hidden (mantiene el
   * width/height = LayoutParams, no draws nothing). Paridad Android
   * XOneImageView.java:479. Idempotente.
   */
  hideBrokenImage(img) {
    img.removeAttribute("src");
    img.setAttribute("alt", "");
    img.style.visibility = "hidden";
    img.classList.add("xone-img-empty");
  }
  normalizeImgSrc(raw) {
    if (!raw || raw.startsWith("http://") || raw.startsWith("https://") || raw.startsWith("data:") || raw.includes("##")) return raw;
    let n = raw.replace(/\\/g, "/");
    if (n.startsWith("./")) n = n.slice(2);
    if (n.startsWith("/")) return n;
    if (n.includes("/")) return `/${n}`;
    return `/icons/${n}`;
  }
  loadExpandChildren(runtime, container, src, filterTemplate, sort, templateHtml, parentItem, cellHeight) {
    let resolvedFilter = filterTemplate;
    if (resolvedFilter.includes("##")) {
      resolvedFilter = resolvedFilter.replace(/##FLD_([A-Za-z0-9_]+)##/g, (_m, field) => {
        const val = parentItem[field];
        return val !== void 0 && val !== null ? String(val) : "";
      });
      resolvedFilter = this.resolveMacros(runtime, resolvedFilter) ?? resolvedFilter;
    }
    const loading = document.createElement("div");
    loading.className = "xone-expand-loading";
    loading.textContent = "Cargando...";
    container.appendChild(loading);
    void (async () => {
      try {
        const coll = runtime.api.appData.getCollection(src);
        await coll.loadAll(resolvedFilter, sort);
        const count = coll.getCount?.() ?? 0;
        container.innerHTML = "";
        for (let i = 0; i < count; i++) {
          const child = coll.get?.(i);
          if (child) {
            this.appendContentRow(container, templateHtml, child, i, cellHeight);
          }
        }
        if (count === 0) {
          container.innerHTML = "<div class='xone-contents-empty' style='padding:8px;font-size:12px;color:#999;'>Sin subelementos</div>";
        }
      } catch {
        container.innerHTML = "<div class='xone-contents-error' style='padding:8px;font-size:12px;color:#c00;'>Error al cargar</div>";
      }
    })();
  }
  /**
   * Header-contents (lista tipo tabla con `header-contents="<Coll>"`): las
   * celdas van a ancho EXACTO (sin encoger, ver CSS `.xone-contents-headed`),
   * así cada título de columna cae sobre su columna de datos. Cuando la tabla
   * es más ancha que el viewport, el listado (`.xone-prop-z`) scrollea en
   * horizontal; la BANDA de cabecera es un hermano fuera de esa caja, por lo
   * que hay que trasladarla en X junto con el scroll para que siga alineada.
   * Idempotente (una vez por island); listener limpiado en disconnectedCallback.
   */
  wireHeaderScrollSync(box) {
    if (this._headerSyncWired) return;
    const headed = this.closest(".xone-contents-headed");
    if (!headed) return;
    const headerInner = headed.querySelector(".xone-contents-header > *");
    if (!headerInner) return;
    this._headerSyncWired = true;
    const sync = () => {
      headerInner.style.transform = `translateX(${-box.scrollLeft}px)`;
    };
    box.addEventListener("scroll", sync, { passive: true });
    this.cleanup.push(() => box.removeEventListener("scroll", sync));
    sync();
  }
  /**
   * Header-contents: hace que el frame de fila (frmContent) CREZCA con todas
   * sus columnas, para que el fondo/hover de fila abarque toda la tabla (no se
   * corte a media tabla al scrollear). Las columnas usan `width:N%` DEL PROPIO
   * frame → si el frame crece, las celdas (% de él) crecen también → bucle. Se
   * rompe la circularidad reescribiendo cada `width:N%` a
   * `calc(var(--xone-hc-base) * N/100)`, con `--xone-hc-base` = ancho VISIBLE
   * de la caja (referencia FIJA, no el frame). Así las celdas dejan de depender
   * del ancho del frame y el frame/Line (max-content, ver CSS) crecen a la suma.
   * Se re-ejecuta en cada render (filas nuevas); las celdas ya convertidas
   * (calc) se saltan. En `resize` solo se actualiza la base (el calc recomputa).
   */
  applyHeaderedTableWidths(box) {
    const headed = this.closest(".xone-contents-headed");
    if (!headed) return;
    const base = box.clientWidth;
    if (base <= 0) return;
    headed.style.setProperty("--xone-hc-base", `${base}px`);
    const toFixedBase = (v) => {
      const m = /^([\d.]+)%$/.exec(v);
      return m ? `calc(var(--xone-hc-base) * ${parseFloat(m[1]) / 100})` : null;
    };
    const rewriteRow = (row) => {
      if (!row) return;
      for (const cell of Array.from(row.children)) {
        const nw = toFixedBase(cell.style.width);
        if (nw) cell.style.width = nw;
        const nml = toFixedBase(cell.style.marginLeft);
        if (nml) cell.style.marginLeft = nml;
        const nmr = toFixedBase(cell.style.marginRight);
        if (nmr) cell.style.marginRight = nmr;
      }
    };
    rewriteRow(headed.querySelector(".xone-contents-header .xone-row"));
    for (const item of Array.from(this.querySelectorAll("article[data-row-index]"))) {
      rewriteRow(item.querySelector(".xone-row"));
    }
    if (!this._hcResizeWired && typeof ResizeObserver !== "undefined") {
      this._hcResizeWired = true;
      const ro = new ResizeObserver(() => {
        const b = box.clientWidth;
        if (b > 0) headed.style.setProperty("--xone-hc-base", `${b}px`);
      });
      ro.observe(box);
      this.cleanup.push(() => ro.disconnect());
    }
  }
  applyContentsLayout(container, viewmode, columns, orientation, _bgcolor) {
    container.classList.add("xone-contents");
    if (viewmode === "expanview" || viewmode === "expandview") {
      container.classList.add("is-expandview");
      container.style.display = "flex";
      container.style.flexDirection = "column";
      container.style.overflowY = "auto";
    } else if (viewmode === "gridview" && orientation === "horizontal") {
      container.classList.add("is-gridview");
      container.style.display = "flex";
      container.style.flexDirection = "row";
      container.style.flexWrap = "nowrap";
      container.style.overflowX = "auto";
      container.style.overflowY = "hidden";
      container.dataset["orientation"] = "horizontal";
      container.style.gap = "0";
    } else if (viewmode === "gridview") {
      container.classList.add("is-gridview");
      container.style.display = "grid";
      container.style.gridTemplateColumns = `repeat(${columns > 0 ? columns : 2}, minmax(0, 1fr))`;
      container.style.gap = "0";
    } else {
      container.classList.add("is-recyclerview");
      container.style.display = "flex";
      if (orientation === "horizontal") {
        container.style.flexDirection = "row";
        container.style.overflowX = "auto";
        container.style.overflowY = "hidden";
        container.style.scrollSnapType = "x mandatory";
        container.dataset["orientation"] = "horizontal";
      } else {
        container.style.flexDirection = "column";
        container.dataset["orientation"] = "vertical";
      }
      container.style.gap = "0";
    }
    this.style.maxHeight = "100%";
    this.style.minHeight = "0";
    this.style.overflow = "hidden";
    container.style.maxHeight = "100%";
    container.style.minHeight = "0";
    if (container.dataset["orientation"] !== "horizontal") {
      if (!container.style.height) container.style.height = "100%";
      container.style.overflowY = "auto";
    }
  }
  /**
   * Expande macros ##FLD_X## en atributos DOM de un elemento ya clonado.
   * Paridad Android FieldPropertyValue(prop, attr, bEvaluate=true) →
   * ReplaceFieldValueMacros (XoneDataObject.java:5199-5256).
   *
   * Opera a nivel DOM (no string) para poder ser selectivo: expande
   * src, style, title pero preserva data-reactive-attrs (que necesita
   * el template original para re-evaluación futura).
   */
  expandItemFldMacros(el, item) {
    const fldRe = /##FLD_([A-Za-z0-9_]+)##/g;
    const resolve = (s) => s.replace(fldRe, (_m, field) => {
      const val = item[field];
      return val !== void 0 && val !== null ? String(val) : "";
    });
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    for (let tn = walker.nextNode(); tn !== null; tn = walker.nextNode()) {
      const txt = tn.nodeValue;
      if (txt !== null && txt.includes("##FLD_")) {
        tn.nodeValue = resolve(txt);
      }
    }
    for (const node of [el, ...el.querySelectorAll("[data-reactive-attrs]")]) {
      if (!(node instanceof HTMLElement)) continue;
      const rawDra = node.getAttribute("data-reactive-attrs");
      if (rawDra === null) continue;
      let bindings;
      try {
        bindings = JSON.parse(rawDra);
      } catch {
        continue;
      }
      let allResolved = true;
      for (const b of bindings) {
        if (!b || typeof b.tpl !== "string") continue;
        const resolved = resolve(b.tpl);
        if (resolved.includes("##")) {
          allResolved = false;
          continue;
        }
        this.applyRowReactiveAttr(node, b.attr, resolved);
      }
      if (allResolved) node.removeAttribute("data-reactive-attrs");
    }
    const ATTRS_TO_EXPAND = ["src", "style", "title", "alt"];
    const allEls = el.querySelectorAll("*");
    const targets = [el, ...allEls];
    for (const node of targets) {
      if (!(node instanceof HTMLElement)) continue;
      for (const attrName of ATTRS_TO_EXPAND) {
        const val = node.getAttribute(attrName);
        if (val && val.includes("##FLD_")) {
          const resolved = resolve(val);
          node.setAttribute(attrName, resolved);
        }
      }
      if (node.tagName === "IMG") {
        const tpl = node.getAttribute("data-xone-img-src");
        if (tpl !== null) {
          const resolved = resolve(tpl).trim();
          node.removeAttribute("data-xone-img-src");
          if (resolved === "" || resolved.includes("##")) {
            this.hideBrokenImage(node);
          } else {
            node.src = this.normalizeImgSrc(resolved);
            node.style.removeProperty("visibility");
            node.classList.remove("xone-img-empty");
          }
          continue;
        }
        const src = node.getAttribute("src") || "";
        if (src.trim() === "" || src.includes("##")) {
          this.hideBrokenImage(node);
        } else if (!src.startsWith("/") && !src.startsWith("http") && !src.startsWith("data:")) {
          node.src = this.normalizeImgSrc(src);
        }
      }
    }
  }
  appendContentRow(container, templateHtml, item, index, cellHeight) {
    const row = document.createElement("article");
    row.className = "xone-content-item xone-contents-row";
    row.innerHTML = templateHtml;
    this.expandItemFldMacros(row, item);
    if (container.classList.contains("is-gridview")) {
      if (container.dataset["orientation"] === "horizontal") {
        for (const child of row.children) {
          if (child instanceof HTMLElement && child.style.width && child.style.width !== "auto") {
            row.style.flex = `0 0 ${child.style.width}`;
            row.style.width = child.style.width;
            child.style.width = "100%";
            row.style.containerType = "size";
            break;
          }
        }
      } else {
        row.style.minWidth = "0";
        for (const child of row.children) {
          if (child instanceof HTMLElement && child.style.width !== "") {
            child.style.width = "auto";
            child.style.alignSelf = "stretch";
          }
        }
      }
    }
    if (cellHeight && cellHeight !== "-2") {
      const numMatch = cellHeight.match(/^(\d+)p$/);
      if (numMatch) {
        row.style.height = `calc(${numMatch[1]} * var(--xone-p-unit, 1px))`;
        row.style.flexShrink = "0";
        row.style.overflow = "hidden";
      }
    }
    const propEls = row.querySelectorAll("[data-prop]");
    for (const el of propEls) {
      const field = el.getAttribute("data-prop");
      if (!field) continue;
      const value = item[field];
      if (value === void 0 || value === null) continue;
      const str = String(value);
      const labelEl = el.querySelector(".xone-label-text");
      if (labelEl) {
        labelEl.textContent = str;
        continue;
      }
      const textEl = el.querySelector(".xone-text-value");
      if (textEl) {
        textEl.textContent = str;
        continue;
      }
      const img = el.tagName === "IMG" ? el : el.querySelector("img");
      if (img && str) {
        img.src = str;
        continue;
      }
      const input = el.querySelector("input,textarea");
      if (input) {
        input.value = str;
        continue;
      }
      if (el.tagName === "SPAN" || el.tagName === "DIV") {
        el.textContent = str;
      }
    }
    const zebraHost = container.closest("[data-cell-even-color]");
    const evenColor = zebraHost?.getAttribute("data-cell-even-color") ?? "";
    const oddColor = zebraHost?.getAttribute("data-cell-odd-color") ?? "";
    if (evenColor !== "" && oddColor !== "") {
      row.style.backgroundColor = xoneColorToCss(index % 2 === 0 ? evenColor : oddColor);
    }
    this.applyRowDisableVisible(row, item);
    this.applyRowDisableEdit(row, item);
    row.setAttribute("data-row-index", String(index));
    container.appendChild(row);
  }
  resolveMacros(runtime, filter) {
    if (!filter || !filter.includes("##")) return filter;
    const macroPattern = /##([A-Za-z0-9_]+)##/g;
    const appData = runtime.api.appData;
    const self = runtime.api.self;
    let result = filter;
    if (typeof appData.expandMacros === "function") {
      result = appData.expandMacros(result);
    }
    if (result.includes("##")) {
      result = result.replace(macroPattern, (_match, name) => {
        try {
          if ((name.startsWith("FLD_") || name.startsWith("OWN_")) && self) {
            const fieldName = name.startsWith("FLD_") ? name.slice(4) : name.slice(4);
            const val = typeof self.getValue === "function" ? self.getValue(fieldName) : self[fieldName];
            if (val !== void 0 && val !== null) return String(val);
            if (typeof appData.getGlobalMacro === "function") {
              const macroVal = appData.getGlobalMacro(fieldName);
              if (macroVal !== void 0 && macroVal !== null) return String(macroVal);
            }
          }
          if (typeof appData.getGlobalMacro === "function") {
            const val = appData.getGlobalMacro(name);
            if (val !== void 0 && val !== null) return String(val);
          }
        } catch {
        }
        return `##${name}##`;
      });
    }
    return result;
  }
  wireSelectedItem(runtime, propName, container) {
    if (this._selectedItemWired) return;
    const contentsName = this.getAttribute("data-contents-name") ?? "";
    const sanitized = contentsName.replace(/^@/, "_");
    const srcColl = this.getAttribute("data-src") ?? "";
    const handler = (sanitized ? runtime.events[`${sanitized}_selecteditem`] : void 0) ?? runtime.events[`${propName}_selecteditem`] ?? (srcColl ? runtime.eventsByColl?.[srcColl]?.["selecteditem"] : void 0) ?? runtime.events["selecteditem"];
    const hasHandler = typeof handler === "function";
    if (!hasHandler) return;
    const target = container ?? this;
    const onClick = (e) => {
      const evTarget = e.target;
      if (evTarget?.closest?.('[data-stop-propagation="true"]')) return;
      if (evTarget?.closest?.("button, input, select, textarea, a[href]")) return;
      const row = evTarget.closest?.(".xone-contents-row");
      if (!row) return;
      const index = Number(row.getAttribute("data-row-index") ?? "0");
      const fromLayout = !this.closest("[data-xone-router-slot]");
      const shellOverlay = runtime.api.shellOverlay;
      void (async () => {
        if (fromLayout) shellOverlay?.beginSelection();
        try {
          let itemSelf = void 0;
          if (srcColl) {
            const coll = runtime.api.appData.getCollection(srcColl);
            itemSelf = coll?.get?.(index);
          }
          const selfForGlobals = itemSelf && typeof itemSelf === "object" ? itemSelf : runtime.api.self;
          const restoreSelf = setXoneGlobalSelf(selfForGlobals);
          try {
            if (itemSelf && typeof itemSelf === "object") {
              const itemApi = {
                ...runtime.api,
                self: itemSelf,
                This: itemSelf
              };
              await handler(itemApi, index);
            } else {
              await handler(runtime.api, index);
            }
            maybeOpenPushedObject(runtime, fromLayout);
          } finally {
            restoreSelf();
          }
        } catch (err) {
          try {
            runtime.api.console.error("[xone-island] selecteditem error", err);
          } catch {
          }
        } finally {
          if (fromLayout) shellOverlay?.endSelection();
        }
      })();
    };
    target.addEventListener("click", onClick);
    this.cleanup.push(() => target.removeEventListener("click", onClick));
    this._selectedItemWired = true;
    void propName;
  }
};
function xoneColorToCss(raw) {
  const c = raw.trim();
  const m = /^#([0-9a-fA-F]{8})$/.exec(c);
  if (m === null) return c;
  const hex = m[1];
  const a = parseInt(hex.slice(0, 2), 16) / 255;
  const r = parseInt(hex.slice(2, 4), 16);
  const g = parseInt(hex.slice(4, 6), 16);
  const b = parseInt(hex.slice(6, 8), 16);
  return `rgba(${r}, ${g}, ${b}, ${a.toFixed(3)})`;
}
function maybeOpenPushedObject(runtime, fromLayout) {
  try {
    const appData = runtime.api.appData;
    if (typeof appData.popValue !== "function") return;
    const v = appData.popValue();
    if (v === void 0 || v === null) return;
    const obj = v;
    const isDataObject = typeof obj === "object" && typeof obj.getValue === "function" && typeof obj.getOwnerCollection === "function";
    if (!isDataObject) {
      appData.pushValue?.(v);
      return;
    }
    const ui = runtime.api.ui;
    ui.openEditView?.(v, fromLayout ? { rootSwitch: true } : void 0);
  } catch {
  }
}
function setXoneGlobalSelf(self) {
  try {
    const w = typeof window !== "undefined" ? window : globalThis;
    const setter = w["__xoneSetSelf"];
    const getter = w["__xoneGetSelf"];
    if (typeof setter !== "function") return () => {
    };
    const prev = typeof getter === "function" ? getter() : void 0;
    setter(self);
    return () => {
      try {
        setter(prev);
      } catch {
      }
    };
  } catch {
    return () => {
    };
  }
}
async function invokeHandler(runtime, handler, args, apiOverride) {
  const api = apiOverride ?? runtime.api;
  const restoreSelf = setXoneGlobalSelf(api.self);
  try {
    await handler(api, ...args);
    maybeOpenPushedObject(runtime);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    try {
      runtime.api.console.error("[xone-island] handler error:", msg, err);
    } catch {
      console.error("[xone-island] handler error:", err);
    }
    try {
      const ui = runtime.api.ui;
      ui?.showToast?.(`Error: ${msg}`);
    } catch {
    }
  } finally {
    restoreSelf();
  }
}
function runXoneNode(name, extraArgs) {
  if (typeof window === "undefined") return;
  const runtime = window[RUNTIME_KEY];
  if (!runtime) return;
  let nodeName = String(name ?? "").trim();
  let args = extraArgs ?? [];
  const m = /^([^(]+)\((.*)\)\s*$/.exec(nodeName);
  if (m && m[1] !== void 0) {
    nodeName = m[1].trim();
    const inner = (m[2] ?? "").trim();
    if (inner !== "" && (extraArgs === void 0 || extraArgs.length === 0)) {
      args = inner.split(",").map((raw) => {
        const t = raw.trim().replace(/^["']|["']$/g, "");
        const n = Number(t);
        return t !== "" && !Number.isNaN(n) ? n : t;
      });
    }
  }
  if (nodeName === "") return;
  let handler = runtime.events?.[nodeName];
  if (typeof handler !== "function") {
    const collName = window.__xone?.collection ?? "";
    const fromColl = collName ? runtime.eventsByColl?.[collName]?.[nodeName] : void 0;
    if (typeof fromColl === "function") handler = fromColl;
  }
  if (typeof handler === "function") {
    void invokeHandler(runtime, handler, args);
    return;
  }
  const g = typeof window !== "undefined" ? window : globalThis;
  const globalFn = g[nodeName];
  if (typeof globalFn === "function") {
    void invokeHandler(
      runtime,
      () => globalFn(runtime.api.self, ...args),
      []
    );
    return;
  }
  try {
    runtime.api.console.warn(`[xone] executeActionAfterDelay: acci\xF3n '${nodeName}' no encontrada (ni nodo ni funci\xF3n global)`);
  } catch {
  }
}
var elementRegistered = false;
function registerXoneIsland(opts) {
  if (typeof window === "undefined") {
    return;
  }
  const runtime = {
    api: opts.api,
    events: opts.events,
    ...opts.eventsByColl ? { eventsByColl: opts.eventsByColl } : {}
  };
  window[RUNTIME_KEY] = runtime;
  window.__xoneRunNode = runXoneNode;
  const layoutOnback = opts.layoutColl ? opts.eventsByColl?.[opts.layoutColl]?.["onback"] : void 0;
  if (typeof layoutOnback === "function") {
    window.__xoneLayoutOnback = () => {
      void invokeHandler(runtime, layoutOnback, []);
    };
  } else {
    try {
      delete window.__xoneLayoutOnback;
    } catch {
      window.__xoneLayoutOnback = void 0;
    }
  }
  if (elementRegistered) return;
  elementRegistered = true;
  if (typeof customElements !== "undefined" && !customElements.get(ELEMENT_TAG)) {
    customElements.define(ELEMENT_TAG, XoneIslandElement);
  }
}
function isInHiddenAncestor(el) {
  let node = el;
  while (node) {
    if (node instanceof HTMLElement) {
      if (node.hasAttribute("hidden")) return true;
      const cs = node.ownerDocument?.defaultView?.getComputedStyle(node);
      if (cs && cs.display === "none") return true;
    }
    node = node.parentElement;
  }
  return false;
}
function hydrateVisibleContentsNow() {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  const vh = window.innerHeight || 0;
  const vw = window.innerWidth || 0;
  const nodes = document.querySelectorAll(ELEMENT_TAG);
  for (const el of Array.from(nodes)) {
    if (!(el instanceof XoneIslandElement)) continue;
    if (!el.hasAttribute("data-src")) continue;
    if ((el.getAttribute("data-type") ?? "").toLowerCase() === "widget") continue;
    if (isInHiddenAncestor(el)) continue;
    const r = el.getBoundingClientRect();
    const inView = r.bottom > 0 && r.top < vh && r.right > 0 && r.left < vw && r.width > 0 && r.height > 0;
    if (!inView) continue;
    el.hydrateNow();
  }
}
function _resetXoneIslandForTests() {
  if (typeof window !== "undefined") {
    delete window[RUNTIME_KEY];
  }
  elementRegistered = false;
}

// src/XoneCombo.ts
var RUNTIME_KEY2 = "__xoneIslandRuntime";
var SEARCH_THRESHOLD = 10;
var HYDRATED2 = /* @__PURE__ */ Symbol("xone-combo-hydrated");
var ELEMENT_TAG2 = "xone-combo";
var XoneComboElement = class extends HTMLElement {
  [HYDRATED2] = false;
  cleanup = [];
  /** Listeners atados mientras el panel está abierto (se limpian en closePanel). */
  panelCleanup = [];
  options = [];
  optionsLoaded = false;
  panel = null;
  lastFilter = null;
  /** Apertura async en vuelo (guard de reentrancia). */
  opening = false;
  connectedCallback() {
    if (this[HYDRATED2]) return;
    this.tryHydrate(0);
  }
  disconnectedCallback() {
    this.closePanel();
    for (const fn of this.cleanup) {
      try {
        fn();
      } catch {
      }
    }
    this.cleanup = [];
  }
  tryHydrate(attempt) {
    const runtime = window[RUNTIME_KEY2];
    if (!runtime) {
      if (attempt > 40) return;
      const t = window.setTimeout(() => this.tryHydrate(attempt + 1), 250);
      this.cleanup.push(() => window.clearTimeout(t));
      return;
    }
    if (this[HYDRATED2]) return;
    this[HYDRATED2] = true;
    try {
      this.hydrate(runtime);
      this.setAttribute("hydrated", "");
    } catch (err) {
      try {
        runtime.api.console.error("[xone-combo] hydrate", err);
      } catch {
      }
    }
  }
  // ---------- atributos ----------
  get displayProp() {
    return this.getAttribute("data-prop") ?? "";
  }
  get idProp() {
    return this.getAttribute("data-combo-idprop") ?? "";
  }
  get linkedfield() {
    return this.getAttribute("data-combo-linkedfield") ?? "";
  }
  get mapfld() {
    return this.getAttribute("data-combo-mapfld") ?? "";
  }
  get coll() {
    return this.getAttribute("data-combo-coll") ?? "";
  }
  get filter() {
    return this.getAttribute("data-combo-filter") ?? "";
  }
  get mode() {
    return this.getAttribute("data-combo-mode") ?? "inline";
  }
  get isStatic() {
    return this.getAttribute("data-combo-coll") === null;
  }
  get hasSearch() {
    return this.mode === "picker" || this.getAttribute("data-combo-search") === "true";
  }
  get hasCleaner() {
    return this.getAttribute("data-combo-cleaner") === "true";
  }
  get defaultText() {
    return this.getAttribute("data-combo-default") ?? "";
  }
  get idIsNumeric() {
    return this.getAttribute("data-combo-id-numeric") === "true";
  }
  get mirrors() {
    const raw = this.getAttribute("data-combo-mirrors");
    if (!raw) return [{ prop: this.displayProp, linkedfield: this.linkedfield }];
    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) && arr.length > 0 ? arr : [{ prop: this.displayProp, linkedfield: this.linkedfield }];
    } catch {
      return [{ prop: this.displayProp, linkedfield: this.linkedfield }];
    }
  }
  get staticValues() {
    const raw = this.getAttribute("data-combo-values");
    if (!raw) return [];
    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  }
  // ---------- binding target ----------
  resolveTarget(runtime) {
    const row = this.closest(".xone-contents-row");
    if (!row) return runtime.api.self ?? {};
    const host = this.closest('xone-island[data-type="Z"]');
    const src = host?.getAttribute("data-src") ?? "";
    const idx = Number(row.getAttribute("data-row-index") ?? "0");
    const coll = src ? runtime.api.appData.getCollection(src) : null;
    const item = coll?.get?.(idx);
    return item && typeof item === "object" ? item : runtime.api.self ?? {};
  }
  readField(runtime, field) {
    try {
      return this.resolveTarget(runtime).getValue?.(field);
    } catch {
      return void 0;
    }
  }
  // ---------- hidratación ----------
  hydrate(runtime) {
    const native = this.querySelector("select.xone-combo-native");
    if (this.isStatic && native) {
      this.wireNativeSelect(runtime, native);
      return;
    }
    this.wireCustomCombo(runtime);
  }
  /** Estático: `<select>` nativo. Reusa write-back directo. */
  wireNativeSelect(runtime, select) {
    const current = this.readField(runtime, this.displayProp);
    if (current !== void 0 && current !== null && current !== "") {
      select.value = String(current);
    }
    const onChange = () => {
      const label = select.value;
      const index = select.selectedIndex;
      this.commitStatic(runtime, label, index);
    };
    select.addEventListener("change", onChange);
    this.cleanup.push(() => select.removeEventListener("change", onChange));
  }
  /** Dinámico / estático-custom: display + trigger + panel. */
  wireCustomCombo(runtime) {
    const display = this.querySelector(".xone-combo-display");
    const trigger = this.querySelector(".xone-combo-trigger");
    this.paintDisplay(runtime);
    const open = (ev) => {
      ev.preventDefault();
      if (this.isDisabled()) return;
      void this.openPanel(runtime);
    };
    trigger?.addEventListener("click", open);
    display?.addEventListener("click", open);
    this.cleanup.push(() => {
      trigger?.removeEventListener("click", open);
      display?.removeEventListener("click", open);
    });
  }
  isDisabled() {
    return this.hasAttribute("hidden") || this.hasAttribute("disabled") || this.hasAttribute("inert");
  }
  paintDisplay(runtime) {
    const display = this.querySelector(".xone-combo-display");
    if (!display) return;
    const val = this.readField(runtime, this.displayProp);
    const text = val === void 0 || val === null || val === "" ? this.defaultText : String(val);
    display.textContent = text;
    display.classList.toggle("xone-combo-placeholder", text === this.defaultText && text !== "");
  }
  // ---------- opciones ----------
  async ensureOptions(runtime) {
    if (this.isStatic) {
      if (this.optionsLoaded) return;
      const vals = this.staticValues;
      this.options = vals.map((label, index) => ({ value: label, label, index }));
      this.optionsLoaded = true;
      return;
    }
    const resolved = this.resolveMacros(runtime, this.filter);
    if (this.optionsLoaded && resolved === this.lastFilter) return;
    this.lastFilter = resolved ?? "";
    const coll = runtime.api.appData.getCollection(this.coll);
    if (!coll) {
      this.options = [];
      this.optionsLoaded = true;
      return;
    }
    try {
      if (resolved && coll.setFilter) coll.setFilter(resolved);
      await coll.loadAll(resolved ? resolved : void 0);
      const n = coll.getCount();
      const opts = [];
      for (let i = 0; i < n; i++) {
        const obj = coll.get(i);
        if (!obj || typeof obj.getValue !== "function") continue;
        const value = obj.getValue(this.mapfld);
        const label = obj.getValue(this.linkedfield);
        opts.push({
          value: value === void 0 || value === null ? "" : String(value),
          label: label === void 0 || label === null ? "" : String(label),
          obj
        });
      }
      this.options = opts;
      this.optionsLoaded = true;
    } catch (err) {
      try {
        runtime.api.console.error("[xone-combo] loadOptions", err);
      } catch {
      }
      this.options = [];
      this.optionsLoaded = true;
    }
  }
  /** Resuelve macros `##FLD_x##`/`##X##` del filtro contra el objeto actual. */
  resolveMacros(runtime, filter) {
    if (!filter) return void 0;
    if (!filter.includes("##")) return filter;
    const appData = runtime.api.appData;
    const self = this.resolveTarget(runtime);
    let result = filter;
    if (typeof appData.expandMacros === "function") {
      result = appData.expandMacros(result);
    }
    if (result.includes("##")) {
      result = result.replace(/##([A-Za-z0-9_]+)##/g, (_m, name) => {
        try {
          if (name.startsWith("FLD_") || name.startsWith("OWN_")) {
            const f = name.slice(4);
            const v = typeof self.getValue === "function" ? self.getValue(f) : self[f];
            if (v !== void 0 && v !== null) return String(v);
          }
          if (typeof appData.getGlobalMacro === "function") {
            const v = appData.getGlobalMacro(name);
            if (v !== void 0 && v !== null) return String(v);
          }
        } catch {
        }
        return `##${name}##`;
      });
    }
    return result;
  }
  // ---------- panel / modal ----------
  async openPanel(runtime) {
    if (this.opening || this.panel) return;
    this.opening = true;
    this.closePanel();
    const isPicker = this.mode === "picker";
    const panel = document.createElement("div");
    panel.className = isPicker ? "xone-combo-modal" : "xone-combo-panel";
    panel.setAttribute("role", "listbox");
    const dialog = this.getAttribute("data-combo-dialog");
    if (dialog) panel.setAttribute("style", dialog);
    let overlay = null;
    if (isPicker) {
      overlay = document.createElement("div");
      overlay.className = "xone-combo-overlay";
      overlay.appendChild(panel);
    }
    let search = null;
    const list = document.createElement("ul");
    list.className = "xone-combo-options";
    panel.appendChild(list);
    const renderOptions = (query) => {
      list.textContent = "";
      const q = query.trim().toLowerCase();
      const rows = [];
      if (this.hasCleaner && q === "") {
        rows.push({ value: "", label: this.defaultText || "\u2014", index: -1 });
      }
      for (const o of this.options) {
        if (q !== "" && !o.label.toLowerCase().includes(q)) continue;
        rows.push(o);
      }
      for (const o of rows) {
        const li = document.createElement("li");
        li.className = "xone-combo-option";
        li.setAttribute("role", "option");
        li.textContent = o.label === "" ? "\xA0" : o.label;
        li.addEventListener("click", () => {
          if (this.isStatic) this.commitStatic(runtime, o.label, o.index ?? -1);
          else this.commitDynamic(runtime, o);
          this.closePanel();
        });
        list.appendChild(li);
      }
      if (rows.length === 0) {
        const li = document.createElement("li");
        li.className = "xone-combo-option xone-combo-empty";
        li.textContent = this.optionsLoaded ? "Sin resultados" : "Cargando\u2026";
        list.appendChild(li);
      }
    };
    const ensureSearchBox = () => {
      if (search) return;
      search = document.createElement("input");
      search.type = "text";
      search.className = "xone-combo-search";
      search.setAttribute("aria-label", "Buscar");
      search.setAttribute("placeholder", "Buscar\u2026");
      search.addEventListener("input", () => renderOptions(search.value));
      panel.insertBefore(search, list);
      window.setTimeout(() => search.focus(), 0);
    };
    if (this.hasSearch) ensureSearchBox();
    if (!isPicker) {
      const rect = this.getBoundingClientRect();
      panel.style.position = "fixed";
      panel.style.left = `${rect.left}px`;
      panel.style.top = `${rect.bottom}px`;
      panel.style.minWidth = `${rect.width}px`;
    }
    const root = overlay ?? panel;
    document.body.appendChild(root);
    this.panel = root;
    this.setAttribute("aria-expanded", "true");
    this.opening = false;
    renderOptions("");
    if (this.optionsLoaded && this.options.length > SEARCH_THRESHOLD) ensureSearchBox();
    void this.ensureOptions(runtime).then(() => {
      if (this.panel !== root) return;
      if (this.options.length > SEARCH_THRESHOLD) ensureSearchBox();
      renderOptions(search ? search.value : "");
    }).catch(() => {
    });
    const onDocClick = (e) => {
      if (root.contains(e.target) || this.contains(e.target)) return;
      this.closePanel();
    };
    const onKey = (e) => {
      if (e.key === "Escape") this.closePanel();
    };
    if (overlay) {
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) this.closePanel();
      });
    }
    let armed = true;
    const tid = window.setTimeout(() => {
      if (armed) document.addEventListener("click", onDocClick);
    }, 0);
    document.addEventListener("keydown", onKey);
    this.panelCleanup.push(() => {
      armed = false;
      window.clearTimeout(tid);
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    });
  }
  closePanel() {
    for (const fn of this.panelCleanup) {
      try {
        fn();
      } catch {
      }
    }
    this.panelCleanup = [];
    if (this.panel) {
      try {
        this.panel.remove();
      } catch {
      }
      this.panel = null;
    }
    this.removeAttribute("aria-expanded");
  }
  // ---------- commit de selección ----------
  commitStatic(runtime, label, index) {
    const target = this.resolveTarget(runtime);
    void target.setValue?.(this.displayProp, label);
    if (this.idProp && this.idIsNumeric && index >= 0) {
      void target.setValue?.(this.idProp, index);
    } else if (this.idProp && !this.idIsNumeric) {
      void target.setValue?.(this.idProp, label);
    }
    this.paintDisplay(runtime);
    this.fireChange(runtime, label);
  }
  commitDynamic(runtime, opt) {
    const target = this.resolveTarget(runtime);
    if (this.idProp) void target.setValue?.(this.idProp, opt.value);
    for (const m of this.mirrors) {
      let v = "";
      if (opt.obj && typeof opt.obj.getValue === "function") {
        v = opt.obj.getValue(m.linkedfield);
      } else if (m.prop === this.displayProp) {
        v = opt.label;
      }
      void target.setValue?.(m.prop, v === void 0 || v === null ? "" : v);
    }
    this.paintDisplay(runtime);
    this.fireChange(runtime, opt.value);
  }
  /** Dispara `{display}_onchange` y el `oneditoraction` del display. */
  fireChange(runtime, value) {
    const collName = this.getAttribute("data-coll") ?? "";
    const key = `${this.displayProp}_onchange`;
    const pageHandler = runtime.events[key];
    const collHandler = collName ? runtime.eventsByColl?.[collName]?.[key] : void 0;
    const handler = typeof pageHandler === "function" ? pageHandler : collHandler;
    if (typeof handler === "function") {
      try {
        void handler(runtime.api, value);
      } catch (err) {
        try {
          runtime.api.console.error("[xone-combo] onchange", err);
        } catch {
        }
      }
    }
    const oea = this.getAttribute("data-oneditoraction");
    if (oea) this.runInlineAction(runtime, oea);
  }
  runInlineAction(runtime, raw) {
    execInlineAction(raw, this.resolveTarget(runtime), (err) => {
      try {
        runtime.api.console.error("[xone-combo] oneditoraction", err);
      } catch {
      }
    });
  }
};
var registered = false;
function registerXoneCombo() {
  if (registered) return;
  registered = true;
  if (typeof customElements !== "undefined" && !customElements.get(ELEMENT_TAG2)) {
    customElements.define(ELEMENT_TAG2, XoneComboElement);
  }
}
function _resetXoneComboForTests() {
  registered = false;
}

// src/canvasSizer.ts
import { installCanvasSizer, installCanvasSizerFromDocument } from "@xone-web/css-engine";
export {
  XoneComboElement,
  XoneIslandElement,
  _resetXoneComboForTests,
  _resetXoneIslandForTests,
  hydrateVisibleContentsNow,
  installCanvasSizer,
  installCanvasSizerFromDocument,
  registerXoneCombo,
  registerXoneIsland,
  runXoneNode
};
//# sourceMappingURL=index.js.map